import { etTradingDay } from './ema-tail';
// Formatters + indicator math, lifted verbatim from the prototype's data.js so the
// components keep calling the same helpers (just imported instead of window.Legend.*).

export const fmtPx = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtPx4 = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: v < 1 ? 4 : 2 });

export const fmtPct = (v: number) => (v >= 0 ? '+' : '') + (v * 100).toFixed(2) + '%';

export const fmtSignedPx = (v: number) =>
  (v >= 0 ? '+' : '−') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtUsd = (v: number) =>
  (v >= 0 ? '+$' : '−$') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const fmtQty = (v: number) => v.toLocaleString('en-US');

export const fmtVol = (v: number) =>
  v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(0) + 'K' : '' + v;

// "12:03:44" in EXCHANGE time (ET), whatever the viewer's own timezone is — the one
// clock convention every timestamp in the terminal follows. `hourCycle:'h23'` and not
// `hour12:false`, which renders midnight as 24:00:00 on en-US.
let _etClockFmt: Intl.DateTimeFormat | null = null;
export function etClock(ts: number): string {
  if (!_etClockFmt) _etClockFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return _etClockFmt.format(new Date(ts));
}

// ---- compact money / shares / dates (supply-dilution card) ----
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtMoneyShort(v: number | null | undefined): string {
  if (v == null) return '—';
  const a = Math.abs(v), s = v < 0 ? '−$' : '$';
  if (a >= 1e9) { const x = a / 1e9; return s + (x >= 100 || x === Math.round(x) ? x.toFixed(0) : x.toFixed(1)) + 'B'; }
  if (a >= 1e6) { const x = a / 1e6; return s + (x >= 100 || x === Math.round(x) ? x.toFixed(0) : x.toFixed(2)) + 'M'; }
  if (a >= 1e3) return s + Math.round(a / 1e3) + 'K';
  return s + Math.round(a);
}

export function fmtSharesShort(v: number | null | undefined): string {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return (a / 1e6).toFixed(2) + 'M';
  if (a >= 1e3) return Math.round(a / 1e3) + 'K';
  return '' + Math.round(a);
}

export const fmtDayMon = (s: string) => { const p = String(s).slice(0, 10).split('-'); return MON[(+p[1]) - 1] + ' ' + (+p[2]); };
export const fmtMonYr = (s: string) => { const p = String(s).slice(0, 10).split('-'); return MON[(+p[1]) - 1] + " '" + p[0].slice(2); };

// pnl given side
export function pnl(side: string, avg: number, last: number, qty: number) {
  const dir = side === 'long' ? 1 : -1;
  const dollar = (last - avg) * dir * qty;
  const pct = avg ? ((last - avg) / avg) * dir : 0;
  return { dollar, pct };
}

// is the position green? long & above OR short & below
export function inProfit(side: string, avg: number, last: number) {
  return side === 'long' ? last >= avg : last <= avg;
}

// ---- indicator math (operate on candle arrays {time, close, high, low, value}) ----
type Candle = { time: number; open?: number; high: number; low: number; close: number; value: number };

export function sma(candles: Candle[], period: number) {
  const out: { time: number; value: number }[] = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= period) sum -= candles[i - period].close;
    if (i >= period - 1) out.push({ time: candles[i].time, value: +(sum / period).toFixed(4) });
  }
  return out;
}

export function vwap(candles: Candle[]) {
  let pv = 0;
  let vv = 0;
  const out: { time: number; value: number }[] = [];
  for (const c of candles) {
    const tp = (c.high + c.low + c.close) / 3;
    pv += tp * c.value;
    vv += c.value;
    out.push({ time: c.time, value: +(pv / vv).toFixed(4) });
  }
  return out;
}

// Simple (arithmetic) moving average of closes — the chart's MA mode. Points
// begin once `period` bars exist, mirroring emaArr's shape.
export function smaArr(candles: Candle[], period: number) {
  const out: { time: number; value: number }[] = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= period) sum -= candles[i - period].close;
    if (i >= period - 1) out.push({ time: candles[i].time, value: +(sum / period).toFixed(4) });
  }
  return out;
}

export function emaArr(candles: Candle[], period: number) {
  const k = 2 / (period + 1);
  let prev = 0;
  const out: { time: number; value: number }[] = [];
  candles.forEach((c, i) => {
    prev = i === 0 ? c.close : c.close * k + prev * (1 - k);
    out.push({ time: c.time, value: +prev.toFixed(4) });
  });
  return out;
}

// Session VWAP + volume-weighted standard-deviation bands (±k1σ inner, ±k2σ outer).
// Typical price = (h+l+c)/3, volume-weighted, anchored per ET trading day (so it's a
// real "session" VWAP that resets daily rather than drifting flat over weeks). The
// center (`vwap`) is the exact midline of the bands. Returns [{time,vwap,u1,l1,u2,l2}].
export function vwapBands(candles: Candle[], k1 = 1, k2 = 2) {
  let pv = 0, vv = 0, p2v = 0, day = '';
  const out: { time: number; vwap: number; u1: number; l1: number; u2: number; l2: number }[] = [];
  for (const c of candles) {
    const d = etTradingDay(c.time);
    if (d !== day) { pv = 0; vv = 0; p2v = 0; day = d; } // anchor a fresh session each ET day
    const tp = (c.high + c.low + c.close) / 3;
    const vol = c.value || 0;
    pv += tp * vol; vv += vol; p2v += tp * tp * vol;
    const mean = vv > 0 ? pv / vv : tp;
    const sd = Math.sqrt(vv > 0 ? Math.max(0, p2v / vv - mean * mean) : 0);
    out.push({
      time: c.time,
      vwap: +mean.toFixed(4),
      u1: +(mean + k1 * sd).toFixed(4), l1: +(mean - k1 * sd).toFixed(4),
      u2: +(mean + k2 * sd).toFixed(4), l2: +(mean - k2 * sd).toFixed(4),
    });
  }
  return out;
}

// ---- incremental live tails: lib/ema-tail.ts (strict, unit-tested) ----
export { makeTailComputer, type EmaTail, type BandTail } from './ema-tail';
